import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, message, AutoComplete } from 'antd';
import { api, fmt, getCurrentUser } from '../api';
import { ROLE_KO, ROLE_OPTIONS, POSITION_OPTIONS } from '../labels';
import { SkeletonTable } from '../components/Skeleton';

type User = {
  id: number; username: string; email: string | null;
  name: string | null; position: string | null;
  role: 'admin' | 'staff'; active: number; must_change_password: number;
  last_login_at: string | null; created_at: string;
};

// 정보성/성공 알림용 사이트 모달 — 결과 안내.
type InfoModalState = {
  title: string;
  body: React.ReactNode;
  variant?: 'success' | 'warning' | 'info';
} | null;

// 이름/직급 수정용 프롬프트 상태.
type EditFieldState = {
  user: User;
  field: 'name' | 'position';
  value: string;
} | null;

export default function Users() {
  const currentUserId = useMemo(() => getCurrentUser()?.id ?? null, []);
  const [list, setList] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);

  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [role, setRole] = useState<'admin'|'staff'>('staff');
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // 사이트 스타일 다이얼로그 상태 (브라우저 confirm/prompt/alert 대체)
  const [info, setInfo] = useState<InfoModalState>(null);
  const [editField, setEditField] = useState<EditFieldState>(null);
  const [savingField, setSavingField] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api<{ data: User[] }>('GET', '/users');
      setList(r.data ?? []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // 서버 오류 메시지 사용자 문구 변환.
  const humanizeError = (e: any): string => {
    const raw = e?.message ?? '';
    const body = e?.body && typeof e.body === 'object' ? e.body : {};
    if (body.message) return String(body.message);
    if (raw === 'last_admin_protected') return '최소 1개의 활성 관리자 계정이 필요합니다. 다른 관리자 계정을 먼저 지정해 주세요.';
    if (raw === 'has_usage_history')    return '사용 이력이 있는 계정입니다. 비활성화를 이용해 주세요.';
    if (raw === 'cannot_delete_self')   return '현재 로그인 중인 계정은 삭제할 수 없습니다.';
    if (raw === 'duplicate_username')   return '이미 등록된 아이디입니다.';
    return raw || '요청 처리 실패';
  };

  const openCreateConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!username.trim() || !/^[a-zA-Z0-9._-]{3,64}$/.test(username.trim())) {
      setErr('아이디는 영문/숫자/._- 3~64자여야 합니다.');
      return;
    }
    setShowConfirmNew(true);
  };
  const doCreate = async () => {
    setCreating(true);
    try {
      const r = await api<{ id: number; temp_password: string }>('POST', '/users',
        { username, name, position, role });
      setShowConfirmNew(false);
      setUsername(''); setName(''); setPosition(''); setRole('staff');
      setShowNew(false);
      await load();
      setInfo({
        title: '신규 운영자 등록 완료',
        variant: 'success',
        body: (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div><b>아이디:</b> <span style={{ fontFamily: 'monospace' }}>{username}</span></div>
            <div>
              <b>임시 비밀번호:</b>{' '}
              <span style={{ fontFamily: 'monospace', background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>{r.temp_password}</span>
            </div>
            <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 12 }}>
              임시 비밀번호를 안전한 채널로 담당자에게 전달해 주세요.<br />
              첫 로그인 시 반드시 새 비밀번호로 변경해야 합니다.
            </div>
          </div>
        ),
      });
    } catch (e: any) {
      setErr(humanizeError(e));
      setShowConfirmNew(false);
    } finally { setCreating(false); }
  };

  const updateField = async (u: User, patch: Record<string, unknown>) => {
    try {
      await api('PUT', `/users/${u.id}`, patch);
      await load();
    } catch (e: any) {
      Modal.error({ title: '변경 실패', content: humanizeError(e), okText: '확인' });
      await load();
    }
  };

  const openEditName = (u: User) =>
    setEditField({ user: u, field: 'name', value: u.name ?? '' });
  const openEditPosition = (u: User) =>
    setEditField({ user: u, field: 'position', value: u.position ?? '' });

  const saveEditField = async () => {
    if (!editField) return;
    setSavingField(true);
    try {
      const patch = editField.field === 'name'
        ? { name: editField.value.trim() || null }
        : { position: editField.value.trim() || null };
      await updateField(editField.user, patch);
      message.success('저장됨');
      setEditField(null);
    } finally { setSavingField(false); }
  };

  const resetPw = (u: User) => {
    Modal.confirm({
      title: `'${u.username}' 계정의 임시 비밀번호 재발급`,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>임시 비밀번호는 <b>무작위 12자리(대/소/숫자)</b>로 생성됩니다 (아이디와 다름).</li>
            <li>다음 로그인 시 <b>강제로 새 비밀번호 변경</b>이 요구됩니다.</li>
            <li>기존 로그인 세션은 유지되나, 만료 후 재로그인 시 새 비밀번호 필요.</li>
          </ul>
        </div>
      ),
      okText: '재발급',
      cancelText: '취소',
      onOk: async () => {
        try {
          const r = await api<{ temp_password: string }>('POST', `/users/${u.id}/reset-password`);
          setInfo({
            title: '임시 비밀번호 재발급 완료',
            variant: 'success',
            body: (
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                <div><b>아이디:</b> <span style={{ fontFamily: 'monospace' }}>{u.username}</span></div>
                <div>
                  <b>임시 비밀번호:</b>{' '}
                  <span style={{ fontFamily: 'monospace', background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>{r.temp_password}</span>
                </div>
                <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 12 }}>
                  안전한 채널로 담당자에게 전달해 주세요. 첫 로그인 시 새 비밀번호로 변경이 필요합니다.
                </div>
              </div>
            ),
          });
        } catch (e: any) {
          Modal.error({ title: '재발급 실패', content: humanizeError(e), okText: '확인' });
        }
      },
    });
  };
  const remove = (u: User) => {
    if (u.id === currentUserId) {
      Modal.warning({ title: '삭제 불가', content: '현재 로그인 중인 계정은 삭제할 수 없습니다.', okText: '확인' });
      return;
    }
    Modal.confirm({
      title: `'${u.username}' 계정 삭제`,
      content: (
        <div style={{ fontSize: 13 }}>
          계정을 삭제하시겠습니까?
          <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12 }}>
            ※ 로그인/보안 이력이 있는 계정은 삭제 대신 <b>비활성화</b>가 적용됩니다.
          </div>
        </div>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await api('DELETE', `/users/${u.id}`);
          message.success('삭제됨');
          await load();
        } catch (e: any) {
          Modal.error({ title: '삭제 실패', content: humanizeError(e), okText: '확인' });
        }
      },
    });
  };

  return (
    <div>
      <h2>계정 관리</h2>
      <div className="card">
        <div className="toolbar">
          <h3 style={{ flex: 1 }}>운영자 목록</h3>
          <button className="btn" onClick={() => setShowNew(s => !s)}>{showNew ? '닫기' : '+ 신규 운영자'}</button>
        </div>

        {showNew && (
          <form onSubmit={openCreateConfirm} style={{ background: 'var(--leaf-50)', padding: 16, borderRadius: 12, marginBottom: 16, border: '1px solid var(--line)' }}>
            <div className="form-grid">
              <div>
                <label>아이디 (영문/숫자/._- 3~64자)</label>
                <input value={username} onChange={e => setUsername(e.target.value)} required pattern="[a-zA-Z0-9._-]{3,64}" style={{ width: '100%' }} />
              </div>
              <div><label>이름</label><input value={name} onChange={e => setName(e.target.value)} style={{ width: '100%' }} /></div>
              <div>
                <label>직급</label>
                <input list="position-list" value={position} onChange={e => setPosition(e.target.value)} placeholder="예: 사원, 대리, 과장" style={{ width: '100%' }} />
                <datalist id="position-list">{POSITION_OPTIONS.map(p => <option key={p} value={p} />)}</datalist>
              </div>
              <div>
                <label>권한</label>
                <select value={role} onChange={e => setRole(e.target.value as any)} style={{ width: '100%' }}
                        title={role === 'admin'
                          ? '관리자: 계정 관리·보안 로그·오류 로그·결제 수신내역/로그·재처리 등 전체 기능 접근'
                          : '스태프: 정산·가맹점 관리·계약 갱신 등 일반 업무 기능 (계정/보안 접근 없음)'}>
                  {ROLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.k}</option>)}
                </select>
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {role === 'admin'
                    ? <>👤 <b>관리자</b> — 정산·가맹점 관리·계약 갱신 + <b>계정 관리·보안 로그·오류 로그·결제 수신내역/수신 로그·실패 재처리</b> 접근 가능</>
                    : <>👤 <b>스태프</b> — 정산·가맹점 관리·계약 갱신·무매출 알림·비밀번호 변경 등 일반 업무 기능 (계정/보안 메뉴 접근 불가)</>}
                </div>
              </div>
            </div>
            <div className="muted" style={{ marginTop: 10 }}>
              임시 비밀번호는 무작위 12자리(대/소/숫자 조합)로 발급됩니다. 첫 로그인 시 반드시 새 비밀번호로 변경해야 합니다.
              {role === 'admin' && <span style={{ marginLeft: 8, color: '#b91c1c', fontWeight: 700 }}>※ 관리자 권한으로 등록됩니다.</span>}
            </div>
            {err && <div className="error">{err}</div>}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn" type="submit">등록</button>
              <button className="btn ghost" type="button" onClick={() => setShowNew(false)}>취소</button>
            </div>
          </form>
        )}

        {loading ? <SkeletonTable rows={5} cols={5} showHeader /> : list.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">👤</div><h4>등록된 운영자가 없습니다</h4></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th className="nowrap">아이디</th>
                <th className="nowrap">이름</th>
                <th className="nowrap">직급</th>
                <th className="nowrap">권한</th>
                <th className="nowrap">상태</th>
                <th className="nowrap">최근 로그인</th>
                <th className="nowrap">생성일</th>
                <th className="nowrap" style={{ width: 1 }}>관리</th>
              </tr></thead>
              <tbody>
                {list.map(u => {
                  const isMe = u.id === currentUserId;
                  const changeRole = (newRole: 'admin' | 'staff') => {
                    if (newRole === u.role) return;
                    const isElevate = newRole === 'admin' && u.role === 'staff';
                    Modal.confirm({
                      title: isElevate
                        ? `'${u.username}' 계정에 관리자 권한 부여`
                        : `'${u.username}' 계정의 관리자 권한 해제`,
                      content: (
                        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                          <div>변경 전: <b>{ROLE_KO[u.role]}</b></div>
                          <div>변경 후: <b style={{ color: isElevate ? '#b91c1c' : undefined }}>{ROLE_KO[newRole]}</b></div>
                          {isElevate && (
                            <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12 }}>
                              관리자는 계정 관리·보안 로그 등 전체 기능에 접근할 수 있습니다.
                            </div>
                          )}
                        </div>
                      ),
                      okText: isElevate ? '관리자 권한 부여' : '스태프로 변경',
                      okType: isElevate ? 'danger' : 'primary',
                      cancelText: '취소',
                      onOk: () => updateField(u, { role: newRole }),
                    });
                  };
                  const toggleActive = () => {
                    const willActivate = !u.active;
                    Modal.confirm({
                      title: `'${u.username}' 계정 ${willActivate ? '활성화' : '비활성화'}`,
                      content: willActivate
                        ? '이 계정으로 로그인이 가능해집니다. 계속하시겠습니까?'
                        : '이 계정의 로그인이 차단됩니다. 계속하시겠습니까?',
                      okText: willActivate ? '활성화' : '비활성화',
                      okType: willActivate ? 'primary' : 'danger',
                      cancelText: '취소',
                      onOk: () => updateField(u, { active: willActivate }),
                    });
                  };
                  return (
                  <tr key={u.id} style={isMe ? { background: '#f0fdf4' } : undefined}>
                    <td className="nowrap" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {u.username}
                      {isMe && <span className="chip" style={{ marginLeft: 6, background: '#dcfce7', color: '#166534', fontSize: 10, padding: '1px 6px' }}>현재 사용자</span>}
                    </td>
                    <td className="nowrap" onClick={() => openEditName(u)} style={{ cursor: 'pointer' }} title="클릭해서 수정">
                      {u.name ?? <span className="muted">— 클릭해서 입력 —</span>}
                    </td>
                    <td className="nowrap" onClick={() => openEditPosition(u)} style={{ cursor: 'pointer' }} title="클릭해서 수정">
                      {u.position ?? <span className="muted">— 클릭해서 입력 —</span>}
                    </td>
                    <td className="nowrap">
                      <select value={u.role} onChange={e => changeRole(e.target.value as any)} style={{ padding: '5px 9px', fontSize: 12 }}
                              title={u.role === 'admin'
                                ? '관리자: 계정 관리·보안 로그·오류 로그·결제 수신내역/로그·재처리 등 전체 접근'
                                : '스태프: 정산·가맹점 관리·계약 갱신 등 일반 업무 (계정/보안 접근 없음)'}>
                        {ROLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{ROLE_KO[o.v]}</option>)}
                      </select>
                    </td>
                    <td className="nowrap">
                      <span className={`chip ${u.active ? 'active' : 'inactive'}`}>
                        <span className="dot" />{u.active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="nowrap">{u.last_login_at ? fmt.date(u.last_login_at) : '-'}</td>
                    <td className="nowrap">{fmt.date(u.created_at)}</td>
                    <td className="nowrap">
                      <div className="row-actions">
                        <button className="btn ghost sm" onClick={toggleActive}>{u.active ? '비활성' : '활성'}</button>
                        <button className="btn ghost sm" onClick={() => resetPw(u)}>비번초기화</button>
                        <button className="btn danger sm" onClick={() => remove(u)} disabled={isMe} title={isMe ? '현재 로그인 중인 계정은 삭제할 수 없습니다.' : undefined}>삭제</button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 이름/직급 수정 모달 — 브라우저 prompt 대체 */}
      <Modal
        open={!!editField}
        title={editField
          ? `${editField.field === 'name' ? '이름' : '직급'} 수정 — ${editField.user.name ?? editField.user.username}`
          : ''}
        onCancel={() => setEditField(null)}
        onOk={saveEditField}
        okText="저장"
        cancelText="취소"
        confirmLoading={savingField}
        destroyOnHidden
        width={420}
      >
        {editField && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              현재 {editField.field === 'name' ? '이름' : '직급'}:
              <b style={{ marginLeft: 4 }}>
                {editField.field === 'name'
                  ? (editField.user.name ?? '(미입력)')
                  : (editField.user.position ?? '(미입력)')}
              </b>
            </div>
            {editField.field === 'position' ? (
              <AutoComplete
                value={editField.value}
                onChange={v => setEditField(f => f ? { ...f, value: String(v) } : f)}
                options={POSITION_OPTIONS.map(p => ({ value: p }))}
                placeholder="예: 사원, 대리, 과장, 팀장 …"
                style={{ width: '100%' }}
                allowClear
              />
            ) : (
              <Input
                value={editField.value}
                onChange={e => setEditField(f => f ? { ...f, value: e.target.value } : f)}
                placeholder="이름을 입력하세요"
                autoFocus
                onPressEnter={saveEditField}
              />
            )}
          </div>
        )}
      </Modal>

      {/* 신규 등록 최종 확인 모달 */}
      <Modal
        open={showConfirmNew}
        title="신규 운영자 등록 최종 확인"
        onCancel={() => !creating && setShowConfirmNew(false)}
        onOk={doCreate}
        okText={role === 'admin' ? '관리자 권한으로 등록' : '스태프로 등록'}
        okType={role === 'admin' ? 'danger' : 'primary'}
        cancelText="취소"
        confirmLoading={creating}
        width={480}
      >
        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
          <div><b>아이디:</b> <span style={{ fontFamily: 'monospace' }}>{username}</span></div>
          <div><b>이름:</b> {name || <span className="muted">(미입력)</span>}</div>
          <div><b>직급:</b> {position || <span className="muted">(미입력)</span>}</div>
          <div>
            <b>권한:</b>{' '}
            <span style={role === 'admin' ? { color: '#b91c1c', fontWeight: 700 } : undefined}>
              {ROLE_KO[role]}{role === 'admin' && ' — 전체 기능 접근 가능'}
            </span>
          </div>
          <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 12 }}>
            등록 후 <b>무작위 임시 비밀번호</b>가 발급됩니다. 첫 로그인 시 새 비밀번호로 변경이 요구됩니다.
          </div>
        </div>
      </Modal>

      {/* 정보성 결과 안내 모달 (등록 완료/재발급 완료 등) */}
      <Modal
        open={!!info}
        title={info?.title}
        onCancel={() => setInfo(null)}
        onOk={() => setInfo(null)}
        okText="확인"
        cancelButtonProps={{ style: { display: 'none' } }}
        destroyOnHidden
        width={480}
      >
        {info?.body}
      </Modal>
    </div>
  );
}
